import { useState, useEffect } from 'react';
import { useAuth } from '../utils/AuthContext';
import { getDataOnRealtimeDb, saveDataOnRealtimeDb, updateDataOnRealtimeDb, deleteDataOnRealtimeDb } from '../utils/firebase';
import { toast } from 'react-toastify';
import moment from 'moment';

export const useBill = () => {
    const [bills, setBills] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        total: 0,
        paid: 0,
        pending: 0,
        overdue: 0
    });
    const { currentUser } = useAuth();

    useEffect(() => {
        fetchBills();
        fetchCategories();
    }, [currentUser]);

    const fetchBills = async () => {
        try {
            const dbBills = await getDataOnRealtimeDb(`users/${currentUser.uid}/bills`);
            if (!dbBills) {
                setBills([]);
                setLoading(false);
                return;
            }

            const billsArray = Object.keys(dbBills).map(key => ({
                id: key,
                name: dbBills[key].name,
                amount: dbBills[key].amount,
                category: dbBills[key].category,
                dueDate: dbBills[key].dueDate,
                description: dbBills[key].description,
                status: dbBills[key].status || 'pending',
                recurring: dbBills[key].recurring || false,
                paymentDate: dbBills[key].paymentDate || null,
                notifyEmail: dbBills[key].notifyEmail || false,
                notifyDays: dbBills[key].notifyDays || 3,
                emailDelivered: dbBills[key].emailDelivered || false
            }));

            setBills(billsArray);
            calculateStats(billsArray);
        } catch (error) {
            console.error("Error fetching bills:", error);
            toast.error("Failed to load bills");
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const categoryData = await getDataOnRealtimeDb(`users/${currentUser.uid}/category/bill`);

            // Transform the data into the required format
            const dbCategories = Object.entries(categoryData).map(([key, value]) => ({
                id: key,
                Category: value
            })).reduce((acc, curr) => {
                acc.push(curr);
                return acc;
            }, []);

            setCategories(dbCategories[0]);
        } catch (error) {
            console.error("Error fetching categories:", error);
            toast.error("Failed to load bill categories");
        }
    };

    const calculateStats = (billList) => {
        const now = moment();

        const stats = billList.reduce((acc, bill) => {
            const amount = parseFloat(bill.amount || 0);
            acc.total += amount;

            if (bill.status === 'paid') {
                acc.paid += amount;
            } else {
                // Check due date for unpaid bills
                const dueDate = moment(bill.dueDate);
                const isOverdue = dueDate.isBefore(now, 'day');

                if (isOverdue) {
                    acc.overdue += amount;
                    // Update bill status to overdue if not already
                    if (bill.status !== 'overdue') {
                        updateBillStatus(bill.id, 'overdue');
                    }
                } else {
                    acc.pending += amount;
                    // If bill was overdue but due date is now in future, update to pending
                    if (bill.status === 'overdue') {
                        updateBillStatus(bill.id, 'pending');
                    }
                }
            }

            return acc;
        }, { total: 0, paid: 0, pending: 0, overdue: 0 });

        setStats(stats);
    };

    const addBill = async (values, { resetForm }) => {
        try {
            const { id, ...billToAdd } = values;
            billToAdd.amount = Math.abs(billToAdd.amount);
            billToAdd.status = 'pending';

            await saveDataOnRealtimeDb(
                `users/${currentUser.uid}/bills`,
                billToAdd
            );

            await fetchBills();
            resetForm();
            return { success: true, message: "Bill added successfully!" };
        } catch (error) {
            console.error('Error adding bill:', error);
            return { success: false, message: "Failed to add bill" };
        }
    };

    const updateBill = async (values, { resetForm }) => {
        try {
            const { id, ...billToUpdate } = values;
            billToUpdate.amount = Math.abs(billToUpdate.amount);

            // Check if bill is not paid and due date is being updated
            if (billToUpdate.status !== 'paid') {
                const newDueDate = moment(billToUpdate.dueDate);
                const today = moment();

                if (newDueDate.isBefore(today, 'day')) {
                    // If new due date is in the past, update status to overdue
                    billToUpdate.status = 'overdue';
                } else {
                    // If new due date is in the future, update status to pending
                    billToUpdate.status = 'pending';
                    billToUpdate.paymentDate = null;
                }
            }

            await updateDataOnRealtimeDb(
                `users/${currentUser.uid}/bills`,
                id,
                billToUpdate
            );

            await fetchBills();
            resetForm();
            return { success: true, message: "Bill updated successfully!" };
        } catch (error) {
            console.error('Error updating bill:', error);
            return { success: false, message: "Failed to update bill" };
        }
    };

    const deleteBill = async (id) => {
        try {
            await deleteDataOnRealtimeDb(`users/${currentUser.uid}/bills`, id);
            await fetchBills();
            return { success: true, message: "Bill deleted successfully!" };
        } catch (error) {
            console.error('Error deleting bill:', error);
            return { success: false, message: "Failed to delete bill" };
        }
    };

    const updateBillStatus = async (id, status, paymentDate = null) => {
        try {
            const bill = bills.find(b => b.id === id);
            if (!bill) return { success: false, message: "Bill not found" };

            const updateData = {
                ...bill,
                status,
                paymentDate: paymentDate || (status === 'paid' ? new Date().toISOString() : null)
            };

            await updateDataOnRealtimeDb(`users/${currentUser.uid}/bills`, id, updateData);

            await fetchBills();
            return { success: true, message: `Bill marked as ${status}` };
        } catch (error) {
            console.error('Error updating bill status:', error);
            return { success: false, message: "Failed to update bill status" };
        }
    };

    return {
        bills,
        categories,
        loading,
        stats,
        addBill,
        updateBill,
        deleteBill,
        updateBillStatus
    };
}; 